/**
 * Rental DTOs with Zod validation - Epic 14
 */
import { z } from 'zod';
import { DiscountType, PricingTier, RentalStatus } from '../interfaces/rental.interface';

// =====================================================
// Rental Checkout DTO - Story 14-1
// =====================================================

export const RentalCheckoutDtoSchema = z
  .object({
    customerId: z
      .string({ required_error: 'Az ügyfél azonosító kötelező' })
      .uuid({ message: 'Érvénytelen ügyfél azonosító formátum' }),
    equipmentId: z
      .string({ required_error: 'A bérgép azonosító kötelező' })
      .uuid({ message: 'Érvénytelen bérgép azonosító formátum' }),
    startDate: z.coerce.date({
      required_error: 'A kezdő dátum kötelező',
      invalid_type_error: 'Érvénytelen dátum formátum',
    }),
    expectedReturnDate: z.coerce.date({
      required_error: 'A várható visszavétel dátuma kötelező',
      invalid_type_error: 'Érvénytelen dátum formátum',
    }),
    depositAmount: z
      .number({ required_error: 'A kaució összeg kötelező' })
      .min(0, 'A kaució összeg nem lehet negatív'),
    notes: z.string().optional(),
    // ADR-078: Optional time handling fields
    startTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'startTime must be in HH:MM format (00:00-23:59)')
      .optional(),
    endTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'endTime must be in HH:MM format (00:00-23:59)')
      .optional(),
    useHourlyBilling: z.boolean().optional(),
    /** Multi-item rental: all equipment items */
    items: z
      .array(
        z.object({
          inventoryItemId: z.string(),
          quantity: z.number().min(1),
        })
      )
      .optional(),
    /**
     * E-105 BE-2 hűségpont-beváltás commit (ADR-103 Am-5 D-5.2). Ha jelen van, a rental-create
     * atomikusan levonja a pontot (redemption-guard unique draftId + negatív ledger +
     * loyaltyRedeemAmount). A pointsApplied/amount a reserve-endpoint (POST /rentals/loyalty/reserve)
     * által visszaadott LOYALTY_REDEEM-sor értékei legyenek (a szerver NEM bízik a kliens-számban
     * vakon — a guard + a ledger SUM a védelem; cap-re-verify a commit-spend jövő-hardening).
     */
    loyaltyRedeem: z
      .object({
        partnerId: z.string().uuid(),
        draftId: z.string().min(1).max(100),
        pointsApplied: z.number().int().positive(),
        amount: z.number().nonnegative(),
      })
      .optional(),
  })
  .refine(data => data.expectedReturnDate > data.startDate, {
    message: 'A visszavétel dátuma későbbi kell legyen a kezdő dátumnál',
    path: ['expectedReturnDate'],
  });

export type RentalCheckoutDto = z.infer<typeof RentalCheckoutDtoSchema>;

export function validateRentalCheckout(data: unknown): RentalCheckoutDto {
  return RentalCheckoutDtoSchema.parse(data);
}

// =====================================================
// Rental Return DTO - Story 14-4
// =====================================================

export const RentalReturnDtoSchema = z
  .object({
    rentalId: z
      .string({ required_error: 'A bérlés azonosító kötelező' })
      .uuid({ message: 'Érvénytelen bérlés azonosító formátum' }),
    returnDate: z.coerce.date({
      required_error: 'A visszavétel dátuma kötelező',
      invalid_type_error: 'Érvénytelen dátum formátum',
    }),
    accessoryChecklistVerified: z.boolean({
      required_error: 'A tartozék ellenőrzőlista megerősítése kötelező',
    }),
    equipmentCondition: z.enum(
      [
        'EXCELLENT',
        'GOOD',
        'FAIR',
        'POOR',
        'DAMAGED',
        'DAMAGED_USABLE',
        'DAMAGED_SERVICE',
        'INCOMPLETE',
      ],
      {
        required_error: 'A bérgép állapota kötelező',
        invalid_type_error: 'Érvénytelen állapot érték',
      }
    ),
    damageNotes: z.string().optional(),
    depositAction: z.enum(['RETURN', 'RETAIN_PARTIAL', 'RETAIN_FULL'], {
      required_error: 'A kaució művelet kötelező',
      invalid_type_error: 'Érvénytelen kaució művelet',
    }),
    retainedAmount: z.number().min(0, 'A visszatartott összeg nem lehet negatív').optional(),
    retentionReason: z.string().optional(),
  })
  .refine(
    data =>
      data.depositAction !== 'RETAIN_PARTIAL' ||
      (data.retainedAmount !== undefined && data.retainedAmount > 0),
    {
      message: 'Részleges visszatartásnál meg kell adni a visszatartott összeget',
      path: ['retainedAmount'],
    }
  )
  .refine(
    data =>
      (data.depositAction !== 'RETAIN_PARTIAL' && data.depositAction !== 'RETAIN_FULL') ||
      data.retentionReason,
    { message: 'Visszatartásnál az indoklás kötelező', path: ['retentionReason'] }
  );

export type RentalReturnDto = z.infer<typeof RentalReturnDtoSchema>;

export function validateRentalReturn(data: unknown): RentalReturnDto {
  return RentalReturnDtoSchema.parse(data);
}

// =====================================================
// P0-2 (6. kör) — return-protokoll H1 contract (contract-first, Botka-unblock)
// =====================================================

/**
 * POST /api/v1/rentals/{rentalId}/return-doc → a return-protokoll-dokumentum.
 * Az esign RENTAL_RETURN session shape-je:
 *   `{ sourceType:'RENTAL_RETURN', sourceId: rentalId, documentId: <ez a documentId> }`
 * A `sourceId` a **rentalId** (a post-sign handler ezen tölti a `Rental.returnSignedAt`-ot —
 * `update where id=sourceId`, lásd rental-return-post-sign.ts). A `documentId` a SigningSession
 * KÜLÖN Document-linkje (a return-doc PDF). ⚠️ A `sourceId` NEM a documentId (silent-fail
 * lenne: `rental.update where id=documentId` → nincs találat → returnSignedAt nem rögzül).
 */
export const ReturnDocResponseSchema = z.object({
  rentalId: z.string().uuid(),
  documentId: z.string().uuid(),
  pdfUrl: z.string(),
  generatedAt: z.coerce.date(),
});
export type ReturnDocResponse = z.infer<typeof ReturnDocResponseSchema>;

/**
 * D3 — per-tartozék visszavételi állapot (accessory-checklist data-wire).
 * A rental-válasz ma csak `accessoryChecklistVerified: boolean`-t ad (operator-attesztáció);
 * a FE-checklisthez a per-tartozék lista + állapot kell.
 * ⚠️ A `kind` a Prisma `PackageItemCategory` enumot tükrözi (FREE | RENTABLE | CONSIGNMENT) —
 * a forrás a `RentalAccessoryCatalogEntry.category` (a `RentalAccessory` modellnek nincs kind-ja).
 * A `status` nullable: a return-inspection-időben az operátor tölti (ma nincs tárolt per-tartozék állapot).
 * A BE data-wire egy következő slice (data-model döntés-függő).
 */
export const RentalAccessoryCheckItemSchema = z.object({
  accessoryId: z.string().uuid(),
  name: z.string(),
  kind: z.enum(['FREE', 'RENTABLE', 'CONSIGNMENT']),
  status: z.enum(['OK', 'MISSING', 'DAMAGED']).nullable(),
});
export type RentalAccessoryCheckItem = z.infer<typeof RentalAccessoryCheckItemSchema>;

/**
 * D3 WRITE-contract — a return-doc generáláskor az operátor checklist-eredménye.
 * A `POST /api/v1/rentals/{id}/return-doc` body-ja: a per-tartozék `status` (itt NEM
 * nullable — a beküldéskor az operátor kitöltötte) + opcionális kondíció-megjegyzés.
 * Ephemeral (Phase 1): a status a visszavételi-jegyzőkönyv PDF-be renderelődik (az
 * aláírt, immutábilis PDF a Phase-1 audit-record); per-tartozék persistence = Phase-2.
 */
export const ReturnAccessoryStatusSchema = z.object({
  accessoryId: z.string().uuid(),
  status: z.enum(['OK', 'MISSING', 'DAMAGED']),
});
export type ReturnAccessoryStatus = z.infer<typeof ReturnAccessoryStatusSchema>;

export const ReturnDocRequestSchema = z.object({
  accessories: z.array(ReturnAccessoryStatusSchema).optional(),
  conditionNotes: z.string().max(2000).optional(),
});
export type ReturnDocRequest = z.infer<typeof ReturnDocRequestSchema>;

// =====================================================
// Rental Extension DTO - Story 14-5
// =====================================================

export const RentalExtensionDtoSchema = z.object({
  rentalId: z
    .string({ required_error: 'A bérlés azonosító kötelező' })
    .uuid({ message: 'Érvénytelen bérlés azonosító formátum' }),
  newReturnDate: z.coerce.date({
    required_error: 'Az új visszavétel dátuma kötelező',
    invalid_type_error: 'Érvénytelen dátum formátum',
  }),
  reason: z.string().optional(),
  selfService: z.boolean().default(false),
  /** FG-2-3: Payment method for extension (if payment required) */
  paymentMethod: z.enum(['CASH', 'CARD']).optional(),
});

export type RentalExtensionDto = z.infer<typeof RentalExtensionDtoSchema>;

export function validateRentalExtension(data: unknown): RentalExtensionDto {
  return RentalExtensionDtoSchema.parse(data);
}

// =====================================================
// Rental Extension By Amount DTO - FG-2-1
// =====================================================

export const RentalExtensionByAmountDtoSchema = z.object({
  rentalId: z
    .string({ required_error: 'A bérlés azonosító kötelező' })
    .uuid({ message: 'Érvénytelen bérlés azonosító formátum' }),
  amount: z
    .number({ required_error: 'Az összeg kötelező' })
    .positive({ message: 'Az összegnek pozitívnak kell lennie' }),
  reason: z.string().optional(),
  selfService: z.boolean().default(false),
});

export type RentalExtensionByAmountDto = z.infer<typeof RentalExtensionByAmountDtoSchema>;

export function validateExtensionByAmount(data: unknown): RentalExtensionByAmountDto {
  return RentalExtensionByAmountDtoSchema.parse(data);
}

// =====================================================
// Price Calculation DTO - Story 14-2
// =====================================================

export const PriceCalculationDtoSchema = z
  .object({
    equipmentId: z
      .string({ required_error: 'A bérgép azonosító kötelező' })
      .uuid({ message: 'Érvénytelen bérgép azonosító formátum' }),
    startDate: z.coerce.date({
      required_error: 'A kezdő dátum kötelező',
      invalid_type_error: 'Érvénytelen dátum formátum',
    }),
    endDate: z.coerce.date({
      required_error: 'A befejező dátum kötelező',
      invalid_type_error: 'Érvénytelen dátum formátum',
    }),
    customerId: z.string().uuid({ message: 'Érvénytelen ügyfél azonosító formátum' }).optional(),
    promoCode: z.string().optional(),
    manualDiscounts: z
      .array(
        z.object({
          type: z.nativeEnum(DiscountType, { required_error: 'A kedvezmény típusa kötelező' }),
          percentage: z
            .number()
            .min(0, 'A százalék nem lehet negatív')
            .max(100, 'A százalék nem lehet 100-nál nagyobb')
            .optional(),
          fixedAmount: z.number().min(0, 'Az összeg nem lehet negatív').optional(),
          reason: z.string({ required_error: 'A kedvezmény indoklása kötelező' }),
        })
      )
      .optional(),
  })
  .refine(data => data.endDate > data.startDate, {
    message: 'A befejező dátum későbbi kell legyen a kezdő dátumnál',
    path: ['endDate'],
  });

export type PriceCalculationDto = z.infer<typeof PriceCalculationDtoSchema>;

export function validatePriceCalculation(data: unknown): PriceCalculationDto {
  return PriceCalculationDtoSchema.parse(data);
}

// =====================================================
// 7. kör H1 — V2-rental multi-item ár-preview kontrakt (contract-first, FE-unblock)
// =====================================================
//
// A V2-wizard jobb-sidebar ár-kalkulációja MA FE-only (`calculateCartPricing`,
// wizard-store.ts) → FE/BE pricing-drift veszély (pénz-helyesség P0). A `POST
// /api/v1/rentals/calculate` preview-endpoint a pricing-et a BE-re viszi (authoritative,
// single source of truth). Ez a request/response shape (Botka erre MSW-mockol day-1; a
// BE-2 a meglévő `calculatePrice` tier/discount/VAT-logikáját aggregálja multi-item-re).

const TIME_HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;

export const RentalCalcItemSchema = z.object({
  equipmentId: z.string().uuid(),
  quantity: z.number().int().positive(),
  dailyRate: z.number().nonnegative(),
  weeklyRate: z.number().nonnegative(),
  monthlyRate: z.number().nonnegative(),
  depositAmount: z.number().nonnegative(),
  hourlyRate: z.number().nonnegative().optional(),
});
export type RentalCalcItem = z.infer<typeof RentalCalcItemSchema>;

export const RentalCalcAccessorySchema = z.object({
  equipmentId: z.string().uuid(),
  accessoryId: z.string().uuid(),
  category: z.enum(['FREE', 'RENTABLE', 'CONSIGNMENT']),
  quantity: z.number().int().positive(),
  dailyRate: z.number().nonnegative().optional(), // RENTABLE
  price: z.number().nonnegative().optional(), // CONSIGNMENT
});
export type RentalCalcAccessory = z.infer<typeof RentalCalcAccessorySchema>;

export const RentalCalcRequestSchema = z.object({
  items: z.array(RentalCalcItemSchema).min(1),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  useHourlyBilling: z.boolean().optional(),
  startTime: z.string().regex(TIME_HHMM, 'HH:MM formátum (00:00–23:59)').optional(),
  endTime: z.string().regex(TIME_HHMM, 'HH:MM formátum (00:00–23:59)').optional(),
  accessories: z.array(RentalCalcAccessorySchema).optional(),
  customerId: z.string().uuid().optional(),
});
export type RentalCalcRequest = z.infer<typeof RentalCalcRequestSchema>;

export const RentalCalcItemResultSchema = z.object({
  equipmentId: z.string().uuid(),
  equipmentName: z.string(),
  tier: z.nativeEnum(PricingTier),
  durationDays: z.number().int().nonnegative(),
  dailyRate: z.number().nonnegative(),
  itemSubtotal: z.number().nonnegative(), // baseAmount × quantity (net, discount előtt)
  itemVat: z.number().nonnegative(),
  itemTotal: z.number().nonnegative(),
  deposit: z.number().nonnegative(),
});
export type RentalCalcItemResult = z.infer<typeof RentalCalcItemResultSchema>;

export const RentalCalcBreakdownDaySchema = z.object({
  date: z.string(), // ISO yyyy-mm-dd
  dayOfWeek: z.number().int().min(0).max(6),
  type: z.enum(['WORKDAY', 'SUNDAY', 'HOLIDAY']),
  value: z.number().nonnegative(),
  holidayName: z.string().optional(),
});
export type RentalCalcBreakdownDay = z.infer<typeof RentalCalcBreakdownDaySchema>;

export const RentalCalcResponseSchema = z.object({
  items: z.array(RentalCalcItemResultSchema),
  subtotal: z.number().nonnegative(), // gépek alapár (volume-discount előtt)
  discountTotal: z.number().nonnegative(), // volume-discount (csak gép-bérletre)
  accessoryRentNet: z.number().nonnegative(), // RENTABLE tartozék netto
  consignmentNet: z.number().nonnegative(), // CONSIGNMENT netto (NEM kedvezményezett)
  vatAmount: z.number().nonnegative(), // 27% × (subtotal − discountTotal + accessoryRentNet)
  grandTotal: z.number().nonnegative(),
  depositTotal: z.number().nonnegative(),
  durationDays: z.number().int().nonnegative(),
  billableDays: z.number().int().nonnegative(),
  weeklyDiscountDays: z.number().int().nonnegative(),
  isMonthlyMode: z.boolean(),
  accessoryCount: z.number().int().nonnegative(),
  breakdownDays: z.array(RentalCalcBreakdownDaySchema),
});
export type RentalCalcResponse = z.infer<typeof RentalCalcResponseSchema>;

// =====================================================
// Apply Discount DTO - Story 14-3
// =====================================================

export const ApplyDiscountDtoSchema = z
  .object({
    rentalId: z
      .string({ required_error: 'A bérlés azonosító kötelező' })
      .uuid({ message: 'Érvénytelen bérlés azonosító formátum' }),
    type: z.nativeEnum(DiscountType, { required_error: 'A kedvezmény típusa kötelező' }),
    name: z
      .string({ required_error: 'A kedvezmény neve kötelező' })
      .min(1, 'A kedvezmény neve kötelező'),
    percentage: z
      .number()
      .min(0, 'A százalék nem lehet negatív')
      .max(100, 'A százalék nem lehet 100-nál nagyobb')
      .optional(),
    fixedAmount: z.number().min(0, 'Az összeg nem lehet negatív').optional(),
    reason: z.string().optional(),
  })
  .refine(data => data.percentage !== undefined || data.fixedAmount !== undefined, {
    message: 'Százalék vagy fix összeg megadása kötelező',
  });

export type ApplyDiscountDto = z.infer<typeof ApplyDiscountDtoSchema>;

export function validateApplyDiscount(data: unknown): ApplyDiscountDto {
  return ApplyDiscountDtoSchema.parse(data);
}

// =====================================================
// Discount Rule DTO - Story 14-3
// =====================================================

export const CreateDiscountRuleDtoSchema = z
  .object({
    type: z.nativeEnum(DiscountType, { required_error: 'A kedvezmény típusa kötelező' }),
    name: z
      .string({ required_error: 'A kedvezmény neve kötelező' })
      .min(1, 'A kedvezmény neve kötelező')
      .max(100, 'A név maximum 100 karakter lehet'),
    description: z.string().optional(),
    percentage: z
      .number()
      .min(0, 'A százalék nem lehet negatív')
      .max(100, 'A százalék nem lehet 100-nál nagyobb')
      .optional(),
    fixedAmount: z.number().min(0, 'Az összeg nem lehet negatív').optional(),
    minDays: z
      .number()
      .int('Egész számnak kell lennie')
      .min(1, 'Minimum 1 nap szükséges')
      .optional(),
    maxDiscountAmount: z.number().min(0, 'Az összeg nem lehet negatív').optional(),
    requiredRole: z.string().optional(),
    promoCode: z.string().optional(),
    validFrom: z.coerce.date({ invalid_type_error: 'Érvénytelen dátum formátum' }).optional(),
    validUntil: z.coerce.date({ invalid_type_error: 'Érvénytelen dátum formátum' }).optional(),
    stackable: z.boolean().default(false),
    priority: z
      .number()
      .int('Egész számnak kell lennie')
      .min(0, 'A prioritás nem lehet negatív')
      .default(100),
  })
  .refine(data => data.percentage !== undefined || data.fixedAmount !== undefined, {
    message: 'Százalék vagy fix összeg megadása kötelező',
  })
  .refine(data => !data.validFrom || !data.validUntil || data.validUntil > data.validFrom, {
    message: 'Az érvényesség vége későbbi kell legyen az érvényesség kezdeténél',
    path: ['validUntil'],
  });

export type CreateDiscountRuleDto = z.infer<typeof CreateDiscountRuleDtoSchema>;

export function validateCreateDiscountRule(data: unknown): CreateDiscountRuleDto {
  return CreateDiscountRuleDtoSchema.parse(data);
}

// =====================================================
// Late Fee Waiver DTO - Story 14-6
// =====================================================

export const WaiveLateFeesDtoSchema = z.object({
  rentalId: z
    .string({ required_error: 'A bérlés azonosító kötelező' })
    .uuid({ message: 'Érvénytelen bérlés azonosító formátum' }),
  reason: z.string({ required_error: 'Az indoklás kötelező' }).min(1, 'Az indoklás kötelező'),
});

export type WaiveLateFeesDto = z.infer<typeof WaiveLateFeesDtoSchema>;

export function validateWaiveLateFees(data: unknown): WaiveLateFeesDto {
  return WaiveLateFeesDtoSchema.parse(data);
}

// =====================================================
// Cancel Rental DTO
// =====================================================

export const CancelRentalDtoSchema = z.object({
  rentalId: z
    .string({ required_error: 'A bérlés azonosító kötelező' })
    .uuid({ message: 'Érvénytelen bérlés azonosító formátum' }),
  reason: z.string({ required_error: 'Az indoklás kötelező' }).min(1, 'Az indoklás kötelező'),
});

export type CancelRentalDto = z.infer<typeof CancelRentalDtoSchema>;

export function validateCancelRental(data: unknown): CancelRentalDto {
  return CancelRentalDtoSchema.parse(data);
}

// =====================================================
// Rental Filter DTO
// =====================================================

export const RentalFilterDtoSchema = z.object({
  status: z.nativeEnum(RentalStatus).optional(),
  customerId: z.string().uuid({ message: 'Érvénytelen ügyfél azonosító formátum' }).optional(),
  equipmentId: z.string().uuid({ message: 'Érvénytelen bérgép azonosító formátum' }).optional(),
  startDateFrom: z.coerce.date({ invalid_type_error: 'Érvénytelen dátum formátum' }).optional(),
  startDateTo: z.coerce.date({ invalid_type_error: 'Érvénytelen dátum formátum' }).optional(),
  overdueOnly: z.boolean().optional(),
  search: z.string().optional(),
  page: z.number().int('Egész számnak kell lennie').min(1, 'Az oldal minimum 1 lehet').default(1),
  pageSize: z
    .number()
    .int('Egész számnak kell lennie')
    .min(1, 'Az oldalméret minimum 1')
    .max(100, 'Az oldalméret maximum 100')
    .default(20),
});

export type RentalFilterDto = z.infer<typeof RentalFilterDtoSchema>;

export function validateRentalFilter(data: unknown): RentalFilterDto {
  return RentalFilterDtoSchema.parse(data);
}

// =====================================================
// Pickup Confirmation DTO - Story 14-1
// =====================================================

export const ConfirmPickupDtoSchema = z.object({
  rentalId: z
    .string({ required_error: 'A bérlés azonosító kötelező' })
    .uuid({ message: 'Érvénytelen bérlés azonosító formátum' }),
  accessoryChecklistVerified: z.boolean({
    required_error: 'A tartozék ellenőrzőlista megerősítése kötelező',
  }),
  depositCollected: z.boolean({ required_error: 'A kaució felvétel státusza kötelező' }),
  depositMethod: z
    .enum(['CASH', 'CARD', 'PRE_AUTH'], {
      invalid_type_error: 'Érvénytelen kaució fizetési mód',
    })
    .optional(),
  notes: z.string().optional(),
});

export type ConfirmPickupDto = z.infer<typeof ConfirmPickupDtoSchema>;

export function validateConfirmPickup(data: unknown): ConfirmPickupDto {
  return ConfirmPickupDtoSchema.parse(data);
}

// =====================================================
// Add Note DTO
// =====================================================

export const AddNoteDtoSchema = z.object({
  rentalId: z
    .string({ required_error: 'A bérlés azonosító kötelező' })
    .uuid({ message: 'Érvénytelen bérlés azonosító formátum' }),
  note: z
    .string({ required_error: 'A megjegyzés kötelező' })
    .min(1, 'A megjegyzés kötelező')
    .max(1000, 'A megjegyzés maximum 1000 karakter lehet'),
});

export type AddNoteDto = z.infer<typeof AddNoteDtoSchema>;

export function validateAddNote(data: unknown): AddNoteDto {
  return AddNoteDtoSchema.parse(data);
}

// =====================================================
// PV-BERLES-001: Részleges-fizetés rögzítése DTO
// =====================================================

/**
 * PATCH /api/v1/rentals/:id/payment — részleges-fizetés rögzítése.
 * A fizetendő (payable) = grandTotal − loyaltyRedeemAmount.
 * Sum-invariáns: paidAmount + outstanding + loyaltyRedeemAmount === grandTotal.
 */
export const RecordPaymentDtoSchema = z.object({
  /** Befizetett összeg (HUF) — pozitív, Decimal-szintű */
  amount: z
    .number({ required_error: 'Az összeg kötelező' })
    .positive('Az összegnek pozitívnak kell lennie'),
  /** Fizetési mód — a meglévő kaució-fizetési módot tükrözi */
  paymentMethod: z.enum(['CASH', 'CARD', 'TRANSFER'], {
    required_error: 'A fizetési mód kötelező',
    invalid_type_error: 'Érvénytelen fizetési mód (CASH | CARD | TRANSFER)',
  }),
  /** Fizetési referencia (bizonylat-szám, banki átutalás-azonosító) */
  paymentReference: z.string().optional(),
  /** Megjegyzés */
  notes: z.string().optional(),
});

export type RecordPaymentDto = z.infer<typeof RecordPaymentDtoSchema>;
